import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  CreditCard, Sparkles, PenLine, BadgeCheck, Truck, Activity,
  FileText, Package, MessageCircle, Mail, ArrowLeft,
} from 'lucide-react';
import BackButton from '../components/BackButton';
import { useT } from '../i18n';

const CONTACT_EMAIL = 'anabatalbook@gmail.com';
const WHATSAPP_LINK = 'https://wa.me/972505020178';

export default function HowItWorks() {
  const t = useT();
  // The ordered post-order journey. Step 5 (delivery) and step 6 (status
  // tracking) render with extra detail blocks, so they're flagged here.
  const STEPS = useMemo(() => [
    { icon: CreditCard, title: t.howItWorksStep1Title, text: t.howItWorksStep1Text },
    { icon: Sparkles, title: t.howItWorksStep2Title, text: t.howItWorksStep2Text },
    { icon: PenLine, title: t.howItWorksStep3Title, text: t.howItWorksStep3Text },
    { icon: BadgeCheck, title: t.howItWorksStep4Title, text: t.howItWorksStep4Text },
    { icon: Truck, title: t.howItWorksStep5Title, text: t.howItWorksStep5Text, delivery: true },
    { icon: Activity, title: t.howItWorksStep6Title, text: t.howItWorksStep6Text, status: true },
  ], [t]);

  // The live order statuses (after payment) the customer will literally see on
  // their order page — kept in sync with i18n status_* labels.
  const STATUS_FLOW = useMemo(() => [
    t.status_paid,
    t.status_in_production,
    t.status_shipped,
    t.status_delivered,
  ], [t]);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="article-page">
      <BackButton fallback="/" />

      <header className="article-hero">
        <span className="article-hero-icon"><Package size={30} /></span>
        <h1 className="article-title">{t.howItWorksTitle}</h1>
        <p className="article-lede">{t.howItWorksLede}</p>
      </header>

      <ol className="process-steps">
        {STEPS.map((step, i) => {
          const Icon = step.icon;
          return (
            <li key={step.title} className="process-step">
              <span className="process-step-num">
                <span className="process-step-index">{i + 1}</span>
                <Icon size={20} aria-hidden="true" />
              </span>
              <div className="process-step-body">
                <h2 className="process-step-title">{step.title}</h2>
                <p className="process-step-text">{step.text}</p>

                {step.delivery && (
                  <div className="process-delivery">
                    <div className="process-delivery-item">
                      <FileText size={18} aria-hidden="true" />
                      <div>
                        <span className="process-delivery-label">{t.howItWorksDeliveryDigital}</span>
                        <span className="process-delivery-time">{t.howItWorksDeliveryDigitalTime}</span>
                      </div>
                    </div>
                    <div className="process-delivery-item">
                      <Package size={18} aria-hidden="true" />
                      <div>
                        <span className="process-delivery-label">{t.howItWorksDeliveryPrint}</span>
                        <span className="process-delivery-time">{t.howItWorksDeliveryPrintTime}</span>
                      </div>
                    </div>
                  </div>
                )}

                {step.status && (
                  <>
                    <div className="process-status-flow">
                      {STATUS_FLOW.map((label, idx) => (
                        <React.Fragment key={label}>
                          <span className="process-status-chip">{label}</span>
                          {idx < STATUS_FLOW.length - 1 && (
                            <ArrowLeft size={15} className="process-status-arrow" aria-hidden="true" />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="process-status-cta">
                      <Link to="/account" className="submit-btn submit-btn-soft">
                        {t.howItWorksStatusCta}
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      <section className="process-help">
        <h2 className="process-help-title">{t.howItWorksHelpTitle}</h2>
        <p className="process-help-text">{t.howItWorksHelpText}</p>
        <div className="process-help-actions">
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="process-help-whatsapp">
            <MessageCircle size={18} aria-hidden="true" /> {t.footerWhatsApp}
          </a>
          <a href={`mailto:${CONTACT_EMAIL}`} className="process-help-email">
            <Mail size={18} aria-hidden="true" /> {CONTACT_EMAIL}
          </a>
        </div>
      </section>
    </div>
  );
}
